// Issue #143 sub-step 5: SDK migration to Amplify v6.
//
// `aws-amplify` no longer exports `API`, `Auth`, `graphqlOperation`,
// `Storage` from the top-level entry. v6 splits those into per-feature
// subpath imports, with the call shapes changed accordingly:
//   - `API.graphql({ query: getProdApplicant, ... })`
//     -> `generateClient().models.ProdApplicant.get({ ... })`
//   - `Storage.list(prefix, { level: "public" })`
//     -> `list({ path: "public/" + prefix })` from `aws-amplify/storage`
//   - `Storage.get(key, { expires })`
//     -> reuse existing `getPresignedUrl(key)` helper from `../utils`
//   - `Storage.put(key, file, { contentType, level: "public" })`
//     -> `uploadData({ path: "public/" + key, data: file,
//                      options: { contentType } }).result`
//
// Q3 from the operator's design answers: coerce `applicantNum`
// int->string only at the SDK boundary (the route-param `id` is a
// string from `useParams()`; the schema `applicantNum` is `a.integer()`).
// We `parseInt(id, 10)` once at the get/update call, leaving every JSX
// site that already string-templates `applicantNum` unchanged.
//
// Q4 lazy-load shape (`hasMany`) is not used in this file -- only
// TeacherView.tsx hits the recommender's prodApplicants relations.
//
// `useHistory` is dropped wholesale; the variable was declared but
// never referenced. Router v6's `useNavigate` is unnecessary here.
//
// `useParams<RouteParam>()` no longer accepts a custom interface generic
// in v6 -- the supported generic is `Record<string, string | undefined>`.
// Cast the result instead, which gives us the same compile-time shape
// the codebase already assumed.
//
// Pre-MR-!42 review: `Auth.currentUserInfo()` -> `getCurrentUser()` from
// `aws-amplify/auth` was on the original migration list, but the only
// consumer was the legacy `if (username === "admin-7w3bh8xp2ld4x")`
// gate inside `AdminTasks`. That gate is now group-based via
// `useAuthGroups()` + `isAdminByGroups`, so the `getCurrentUser()` call
// + `username` state both became dead and were removed. The remaining
// `useAuthenticator` hook supplies the `user` whose email -- resolved via
// `resolveUserEmail` because the Gen 2 email-login pool makes `user.username`
// the sub UUID, not the email -- drives the email-match access check on
// non-admin/grader users.
import { useState, useEffect, Fragment, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import Box, { BoxProps } from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import { getClient } from "../amplify-client";
import { list, uploadData } from "aws-amplify/storage";
import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  ApplicantInfo, affiliatedSchools, AffiliatedSchoolKey
} from "../types";
import Collapsible from "../components/Collapsible";
import Spinner from "../components/Spinner";
import FileViewer from "../components/FileViewer";
import FileUploader from "../components/FileUploader";
import {
  naWrapper,
  canAccessApplicant,
  validateUploadFile,
  getPresignedUrl,
} from "../utils";
import {
  useAuthGroups,
  isAdminByGroups,
  isAdminOrGraderByGroups,
} from "../auth-groups";
import { resolveUserEmail } from "../auth-user";
import ApplicantHeaderWrapper from "../components/ApplicantHeaderWrapper";
import { DataContext, fetchApplicantsList } from "../DataContextProvider";
import { useLang } from "../LanguageContextProvider";

// The data client is constructed lazily at use-time via `getClient()`
// (see `amplify-client.ts`), never at module scope -- a module-scope
// `generateClient()` snapshots the Amplify config before
// `Amplify.configure(...)` runs whenever the bundler evaluates this module
// first, which is the deployed-bundle `Amplify has not been configured`
// regression this fix removes. Untyped pending the TS 5 bump tracked in
// #146.

interface ResponseBlockProps extends BoxProps {
  title: string;
}

const ResponseBlock: React.FC<ResponseBlockProps> = ({
  title,
  children,
  ...rest
}) => (
  <Box paddingBottom="1rem" {...rest}>
    <Typography style={{ fontWeight: "bold" }}>{title}</Typography>
    <Box display="flex" flexDirection="column" padding="0 .25rem">
      {children}
    </Box>
  </Box>
);

const MultiLineResponse: React.FC<{ value: string; keyHeader: string }> = ({
  value,
  keyHeader,
}) => (
  <Fragment>
    {value?.split("\n").map((line, i) => (
      <Typography key={`${keyHeader}-${i}`}>{line}</Typography>
    ))}
  </Fragment>
);

const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
};

const useWindowDimensions = () => {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowDimensions;
};

const Application = () => {
  // The `id` from the path is equivalent of `applicantNum`. v6's
  // `useParams` no longer accepts a custom interface generic; cast the
  // returned record to keep the same compile-time shape (`id` is a
  // string from the `:id` segment, never undefined when this route
  // matches).
  const { id } = useParams() as { id: string };
  const { user } = useAuthenticator((context) => [context.user]);
  const { lang } = useLang();
  // Authoritative source for "is this user an Admin/Grader?" -- the
  // session's `cognito:groups` claim, supplied by `App.tsx` via
  // `AuthGroupsContext` (operator answer Q6 on #143). Pre-MR-!42
  // review: this used to be derived by passing the user's email
  // through `isAdminOrGrader(username)`, which hardcoded the legacy
  // Gen 1 username literals -- the inverse of Q6's intent for any
  // real Admin in the Gen 2 pool.
  const groups = useAuthGroups();
  const isAdminOrGraderUser = isAdminOrGraderByGroups(groups);
  const isAdminUser = isAdminByGroups(groups);
  // This is the object you get from API
  const [detail, setDetail] = useState<ApplicantInfo>();
  // An array of presigned URLs that you asynchronously fetch from the  S3 API
  const [URLs, setURLs] = useState<any[]>([]);
  // The list of tags (options)
  const [tagsOptions, setTagsOptions] = useState<string[]>([]);
  // Local states
  const [tags, setTags] = useState<string[]>([]);
  const [tagsFieldOpen, setTagsFieldOpen] = useState(false);
  const [fileLoading, setFileLoading] = useState<Boolean>(false);
  const [accessDenied, setAccessDenied] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const { height, width } = useWindowDimensions();
  const {
    state: { rawData },
    dispatch,
  } = useContext(DataContext);

  // Fetch the ApplicantInfo object from AppSync
  const fetchTheApplicant = async () => {
    try {
      setLoading(true);
      // v6: typed get via `models.ProdApplicant.get`. Q3 boundary --
      // schema's `applicantNum` is `a.integer()`, but the route param
      // arrives as a string; coerce here at the SDK boundary so every
      // downstream consumer can keep its existing string-shape
      // assumptions. `parseInt` returns NaN for non-numeric URL
      // segments (typo, paste error, stale deep-link); the SDK call
      // returns `{ data: null }` in that case (and for any
      // legitimately-missing applicant -- deleted, wrong cycleYear,
      // race with ingestion). The null-check on the next line catches
      // both paths uniformly so the page doesn't try to render against
      // an undefined detail object further down.
      const res = (await getClient().models.ProdApplicant.get({
        applicantNum: parseInt(id, 10),
      })) as any;
      const applicantDetail = res.data;

      // MR !42 review thread `859dcb10...`: defensive missing-applicant
      // guard. Without this, an admin/grader (who passes
      // `canAccessApplicant`) would proceed to `setDetail(null)` and
      // crash downstream rendering against undefined fields; a
      // recommender would crash inside `canAccessApplicant`'s null
      // dereference (now also defended at the helper level in utils.ts,
      // belt-and-braces). Surfacing as access-denied keeps the existing
      // single-state UX while making the failure path obvious in
      // `git blame` -- a future MR can split this into a 404-style
      // "not found" UI if the distinction matters to operators.
      if (!applicantDetail) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      // Check if user has access to this applicant. The
      // `isAdminOrGraderUser` boolean is derived from the session's
      // `cognito:groups` claim at component-mount time (see top of
      // Application). For non-admin/grader users we still match against
      // their email (the four `recommender*Email` / `schoolReportWriterEmail`
      // columns). In the Gen 2 email-login pool `user.username` is the sub
      // UUID, not the email, so resolve the email via `resolveUserEmail`
      // (matching TeacherView's recommender lookup).
      const userEmail = await resolveUserEmail(user);

      if (!canAccessApplicant(applicantDetail, userEmail, isAdminOrGraderUser)) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      setDetail(applicantDetail);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching applicant:', error);
      setAccessDenied(true);
      setLoading(false);
    }
  };

  // Return an extension format from a given S3 key
  const _parseExtension = (key: string) => {
    const splitKey = key.split(".");
    const ext = splitKey[splitKey.length - 1];
    return ext;
  };

  // Filter files and generate presigned URLs
  const fetchURLs = async (userDetail: ApplicantInfo) => {
    if (userDetail) {
      const theKey = `${userDetail.applicantNum} ${userDetail.name}/`;
      // v5: `Storage.list(theKey, { level: "public" })` auto-prefixed
      //     `public/` and returned `{ results: [{ key: "..." }] }`.
      // v6: `list({ path: "public/<theKey>" })` returns
      //     `{ items: [{ path: "public/...", ... }] }`. Strip the
      //     prefix back off so downstream `s3key`-shaped code stays
      //     unchanged.
      const { items } = await list({ path: `public/${theKey}` });
      const acceptableFiles = items.filter((obj: any) => {
        const keyContainsApplicantNum = obj.path.includes(
          userDetail.applicantNum
        );
        return keyContainsApplicantNum;
      });
      const presignedURLs = await Promise.all(
        acceptableFiles.map(async (item: any) => {
          // v6 `path` is the literal bucket key including the
          // `public/` prefix; `getPresignedUrl` re-prepends `public/`,
          // so feed it the un-prefixed shape v5 callers expect.
          const s3keyWithPrefix: string = item.path;
          const s3key = s3keyWithPrefix.startsWith("public/")
            ? s3keyWithPrefix.slice("public/".length)
            : s3keyWithPrefix;
          const ext = _parseExtension(s3key);
          const presignedURL = await getPresignedUrl(s3key);
          const result: any = {};
          if (ext === "docx" || ext === "xlsx") {
            result.url =
              "https://docs.google.com/viewer?url=" +
              encodeURIComponent(presignedURL) +
              "&embedded=true";
          } else {
            result.url = presignedURL;
          }
          result.filename = s3key.split("/")[1];
          return result;
        })
      );
      setURLs(presignedURLs);
    }
  };

  // Fetch the applicant at the beginning
  useEffect(() => {
    try {
      fetchTheApplicant();
    } catch (e) {
      console.log(`Error fetching the applicant: ${e}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch the tags list at the beginning
  useEffect(() => {
    if (rawData) {
      const tagsList: string[] = [];
      rawData.forEach(({ tags }) => {
        if (tags)
          tags.forEach((tag) => {
            if (tag && !tagsList.includes(tag)) tagsList.push(tag);
          });
      });
      setTagsOptions(tagsList);
    }
  }, [rawData]);

  // Set presigned URLs and set tags state once the `detail` object arrives from AppSync
  useEffect(() => {
    try {
      if (detail) {
        fetchURLs(detail);
        // If there exists tags, set them as the tags state
        if (detail.tags) {
          console.log(detail.tags);
          setTags(detail.tags);
        }
      }
    } catch (e) {
      console.log(`Error fetching the presigned URLs: ${e}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  // Send update API request once tags state changes (except for the initial fetch)
  useEffect(() => {
    const updateTags = async () => {
      // Prevent invoking the API at the beginning
      if (detail && detail.tags !== tags) {
        // v6 typed update via `models.ProdApplicant.update`. `tags` is now
        // modeled on ProdApplicant (`amplify/data/resource.ts`), so this
        // write actually persists via AppSync -- previously `tags` was
        // absent from the schema and AppSync silently stripped it from the
        // updateProdApplicant input, making every tag edit a no-op.
        // `applicantNum` is an `a.integer()` identifier; ApplicantInfo
        // carries it as a string, so coerce to a number for the typed key.
        await getClient().models.ProdApplicant.update({
          applicantNum: Number(detail.applicantNum),
          tags,
        });
      }
    };
    updateTags();
    setTagsFieldOpen(false);
    if (dispatch) fetchApplicantsList(dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags]);

  // Returns FileViewer for a given keyword topic
  const getFileViewer = (topic: string) => {
    const url = URLs.filter((x) => x.filename === `${topic}.pdf`);
    if (url.length === 1) {
      return <FileViewer fileInfo={url[0]} height={height} width={width} />;
    } else if (url.length > 1) {
      var theUrl = url[0];
      for (var x of url) {
        if (x.filename.includes("html")) {
          theUrl = x;
          break;
        } else if (x.filename.includes("pdf")) {
          theUrl = x;
          break;
        }
      }
      return <FileViewer fileInfo={theUrl} height={height} width={width} />;
    } else {
      return <Typography>No uploaded files related to {topic}</Typography>;
    }
  };

  // Uploads a PDF file for this applicant
  const uploadFile = async (e: any, type: string, detail: ApplicantInfo) => {
    // Guard the cancel-the-picker case (modern browsers fire `change`
    // with an empty FileList; older browsers may surface a null
    // `files`). Either way we shouldn't surface an alert -- the user
    // simply chose not to upload anything.
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }
    const file = e.target.files[0];
    const validationError = validateUploadFile(file, lang);
    if (validationError) {
      // Admin path is rare and rerunable; surface via a blocking
      // alert rather than wiring a snackbar. Reset the input so the
      // user can pick the same filename again after they fix it.
      window.alert(validationError);
      e.target.value = "";
      return;
    }
    const filename = `${detail.applicantNum} ${detail.name}/${type}.pdf`;
    console.log(`Uploading ${filename}...`);
    try {
      setFileLoading(true);
      // v5 `Storage.put(filename, file, { contentType, level: "public" })`
      // auto-prefixed `public/`; v6 `uploadData({ path, data, options })`
      // wants the literal access-prefixed path. `.result` awaits the
      // `TransferTaskBase` returned by `uploadData`.
      await uploadData({
        path: `public/${filename}`,
        data: file,
        options: { contentType: "application/pdf" },
      }).result;
      setFileLoading(false);
      console.log("Done!");
    } catch (error) {
      console.log("Error uploading file: ", error);
    }
  };

  const addTag = async (currentTag: string) => {
    console.log(`Adding ${currentTag}`);
    if (currentTag && !tags.includes(currentTag)) {
      setTags((prevTags) => [...prevTags, currentTag]);
    } else {
      // setCurrentTag('');
      setTagsFieldOpen(false);
    }
  };

  const removeTag = async (tag: string) => {
    console.log(`Removing ${tag}`);
    if (tags) {
      const index = tags.indexOf(tag);
      const newTags = [...tags];
      if (index > -1) {
        newTags.splice(index, 1);
      }
      setTags(newTags);
    }
  };

  // Toggle only if it's the admin user. Pre-MR-!42 review: this used to
  // be `if (username === "admin-7w3bh8xp2ld4x")`, which only worked for
  // the single legacy Gen 1 admin account. Now group-based -- any user
  // in the Cognito `Admin` group sees the per-slot file-upload UI.
  const AdminTasks = () => {
    if (isAdminUser) {
      if (fileLoading) {
        return (
          <div>
            <Typography>Uploading...</Typography>
            <Spinner size={50} />
          </div>
        );
      }
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography>Admin: </Typography>
          <FileUploader
            type="推薦状１"
            detail={detail}
            uploadFile={uploadFile}
          />
          <FileUploader
            type="推薦状２"
            detail={detail}
            uploadFile={uploadFile}
          />
          <FileUploader
            type="留学中の調査書"
            detail={detail}
            uploadFile={uploadFile}
          />
          <FileUploader type="調査書" detail={detail} uploadFile={uploadFile} />
        </Stack>
      );
    }
    return <></>;
  };

  const TagsInput = () => {
    const ref = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
      ref.current?.focus();
    }, []);

    return (
      <Fragment>
        <Autocomplete
          freeSolo
          options={tagsOptions}
          // value={currentTag}
          // onInputChange={(e, value) => setCurrentTag(value ?? '')}
          onChange={(e, value) => {
            if (value) addTag(value);
          }}
          onBlur={() => {
            // setCurrentTag('');
            setTagsFieldOpen(false);
          }}
          renderInput={(params) => (
            <TextField
              inputRef={ref}
              variant="standard"
              {...params}
              sx={{ minWidth: "12rem" }}
            />
          )}
        />
        <IconButton
          onClick={() => {
            if (ref.current?.value) addTag(ref.current.value);
          }}
        >
          <AddIcon />
        </IconButton>
      </Fragment>
    );
  };

  if (loading) return <Spinner size={100} />;
  
  if (accessDenied) {
    return (
      <Box
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        padding="2rem"
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography>
            You do not have permission to view this applicant's information. 
            You can only access applicants where you are listed as a recommender or school report writer.
          </Typography>
        </Alert>
      </Box>
    );
  }
  
  if (!detail) return <Spinner size={100} />;
  return (
    <Box
      width="100%"
      height="100%"
      padding="1rem"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Box>
        <Grid container>
          <Grid item xl={1} xs={12} />
          <Grid item xl={10} lg={12}>
            <ApplicantHeaderWrapper
              header={
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  style={{ padding: "0 .5rem" }}
                >
                  <Typography variant="h6">{naWrapper(detail.name)}</Typography>
                  <Typography variant="body1">{naWrapper(detail.englishName)}</Typography>
                  {/* <Typography>{detail.type.slice(0, 4)}</Typography> */}
                  <Typography>
                    {naWrapper(detail.isComplete, "未出願")}
                  </Typography>
                  <Typography>
                    第1志望:{" "}
                    {naWrapper(detail.firstChoice.slice(0, 4), "未選択")}
                  </Typography>
                  <Typography>
                    第2志望:{" "}
                    {naWrapper(detail.secondChoice.slice(0, 4), "未選択")}
                  </Typography>
                </Stack>
              }
            >
              <Grid container>
                <Grid item lg={5} xs={12}>
                  <Box width="100%" padding=".5rem">
                    <Box display="flex" alignItems="center">
                      <Box flex={1} display="flex" flexDirection="column">
                        <Stack direction="row" spacing={5} alignItems="center">
                          {/* <Typography>{detail.type.slice(0, 4)}</Typography> */}
                          <Typography variant="h6">
                            {naWrapper(detail.isComplete, "未出願")}
                          </Typography>
                          <Typography>
                            第1志望:{" "}
                            {naWrapper(
                              detail.firstChoice.slice(0, 4),
                              "未選択"
                            )}
                          </Typography>
                          <Typography>
                            第2志望:{" "}
                            {naWrapper(
                              detail.secondChoice.slice(0, 4),
                              "未選択"
                            )}
                          </Typography>
                        </Stack>
                        <Box display="flex" alignItems="center">
                          <Box display="flex" flexDirection="column" flex={2}>
                            <Typography>{naWrapper(detail.englishName)}</Typography>
                            <Typography>{naWrapper(detail.kana)}</Typography>
                            <Typography variant="h4">
                              {naWrapper(detail.name)}
                            </Typography>
                          </Box>
                          <Box display="flex" flexDirection="column" flex={1}>
                            <Typography>{naWrapper(detail.sex)}</Typography>
                            <Typography>{naWrapper(detail.dob)} 生</Typography>
                          </Box>
                        </Box>
                        <Typography>
                          {naWrapper(detail.highSchool)}
                          {(detail.schoolCourse || detail.schoolDepartment) &&
                            `（${[detail.schoolCourse, detail.schoolDepartment]
                              .filter(Boolean)
                              .join("・")}）`}
                        </Typography>
                      </Box>
                      {/* <img src={detail.imageUrl} height="120px" /> */}
                    </Box>
                  </Box>
                </Grid>
                <Grid item lg={7} xs={12}>
                  <Box
                    height="100%"
                    padding=".5rem"
                    display="flex"
                    flexDirection="column"
                  >
                    <Box flex={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>Tags: </Typography>
                        {tags?.map((tag) => (
                          <Chip
                            key={`tag-${tag}`}
                            label={tag}
                            onDelete={() => removeTag(tag)}
                          />
                        ))}
                        {tagsFieldOpen ? (
                          <TagsInput />
                        ) : (
                          <IconButton onClick={() => setTagsFieldOpen(true)}>
                            <AddIcon />
                          </IconButton>
                        )}
                      </Stack>
                    </Box>
                    <AdminTasks />
                  </Box>
                </Grid>
              </Grid>
            </ApplicantHeaderWrapper>
          </Grid>
          <Grid item xl={1} xs={12} />
        </Grid>
      </Box>
      <Box padding=".5rem 0">
        <Divider />
      </Box>
      <Box flex={1} overflow="auto">
        <Grid container>
          <Grid item xl={1} xs={12} />
          <Grid item xl={10} xs={12}>
            <Grid container>
              <Grid item lg={5} xs={12}>
                <Collapsible header="連絡先">
                  <Box display="flex">
                    <ResponseBlock
                      title="Email"
                      flex={1}
                      display="flex"
                      flexDirection="column"
                    >
                      {naWrapper(detail.email)}
                    </ResponseBlock>
                  </Box>
                </Collapsible>
                <Collapsible header="志望校">
                  <ResponseBlock title="志望３大学とその理由">
                    <Typography style={{ whiteSpace: "pre-line" }}>
                      <MultiLineResponse
                        keyHeader="top3schoolsReasons"
                        value={naWrapper(detail.top3schoolsReasons)}
                      />
                    </Typography>
                  </ResponseBlock>
                  <ResponseBlock title="出願に興味がある提携校">
                    {Object.keys(affiliatedSchools)
                      .filter(
                        (key) => detail.schoolChoice[key as AffiliatedSchoolKey]
                      )
                      .map((key) => (
                        <Typography
                          key={`schools-${
                            affiliatedSchools[key as AffiliatedSchoolKey].symbol
                          }`}
                        >
                          {affiliatedSchools[key as AffiliatedSchoolKey].name}
                        </Typography>
                      ))}
                  </ResponseBlock>
                  <ResponseBlock title="上記以外に出願を考えている大学">
                    <MultiLineResponse
                      keyHeader="schoolChoice"
                      value={naWrapper(detail.schoolChoice.others)}
                    />
                  </ResponseBlock>
                  <ResponseBlock title="希望専攻分野">
                    {naWrapper(detail.intendedMajor)}
                  </ResponseBlock>
                </Collapsible>
                <Collapsible header="学歴/海外在住経験">
                  {/* {getFileViewer("学歴")} */}
                  <MultiLineResponse
                    keyHeader="background"
                    value={naWrapper(detail.background)}
                  />
                  <ResponseBlock title="3ヶ月以上の外国経験">
                    <Typography style={{ whiteSpace: "pre-line" }}>
                      {naWrapper(detail.abroad)}
                    </Typography>
                  </ResponseBlock>
                  <ResponseBlock title="国籍">
                    <Typography style={{ whiteSpace: "pre-line" }}>
                      {naWrapper(detail.nationality)}
                    </Typography>
                  </ResponseBlock>
                </Collapsible>
                <Collapsible header="家族構成">
                  <MultiLineResponse
                    keyHeader="family"
                    value={naWrapper(detail.family)}
                  />
                  <ResponseBlock title="家計支持者の所得金額">
                    {naWrapper(detail.familyIncome)}
                  </ResponseBlock>
                </Collapsible>
                <Collapsible header="テストスコア">
                  <ResponseBlock title="学科テストスコア">
                    <MultiLineResponse
                      keyHeader="otherTestScores"
                      value={naWrapper(detail.otherTestScores)}
                    />
                  </ResponseBlock>
                  <ResponseBlock title="英語テストスコア">
                    <MultiLineResponse
                      keyHeader="englishTestScore"
                      value={naWrapper(detail.englishTestScore)}
                    />
                  </ResponseBlock>
                  <ResponseBlock title="免許・資格">
                    <MultiLineResponse
                      keyHeader="license"
                      value={naWrapper(detail.license?.split("|").join("\n"))}
                    />
                  </ResponseBlock>
                  {getFileViewer("英語テスト")}
                </Collapsible>
              </Grid>
              <Grid item lg={7} xs={12}>
                <Collapsible header="課外活動">
                  <ResponseBlock title="今までの3-5年間で最も注力したこと">
                    <Typography style={{ whiteSpace: "pre-line" }}>
                      <MultiLineResponse
                        keyHeader="churyoku"
                        value={naWrapper(detail.churyoku)}
                      />
                    </Typography>
                  </ResponseBlock>
                  <ResponseBlock title="学校における委員・役員等の経験">
                    <Typography style={{ whiteSpace: "pre-line" }}>
                      <MultiLineResponse
                        keyHeader="yakuin"
                        value={naWrapper(detail.yakuin)}
                      />
                    </Typography>
                  </ResponseBlock>
                  {/* <ResponseBlock title="クラブ活動"> */}
                  <ResponseBlock title="課外活動">
                    <Typography style={{ whiteSpace: "pre-line" }}>
                      <MultiLineResponse
                        keyHeader="club"
                        value={naWrapper(detail.club)}
                      />
                    </Typography>
                  </ResponseBlock>
                </Collapsible>
                <Collapsible header="自己について">
                  <ResponseBlock title="趣味・特技・好きな学科">
                    <Typography style={{ whiteSpace: "pre-line" }}>
                      <MultiLineResponse
                        keyHeader="strength"
                        value={naWrapper(detail.strength)}
                      />
                    </Typography>
                  </ResponseBlock>
                  <ResponseBlock title="自己の性格・アピールポイント">
                    <Typography style={{ whiteSpace: "pre-line" }}>
                      <MultiLineResponse
                        keyHeader="selfPR"
                        value={naWrapper(detail.selfPR)}
                      />
                    </Typography>
                  </ResponseBlock>
                  <ResponseBlock title="現在所属する高校、または卒業した高校を選択した特別な理由 (あれば)">
                    <Typography style={{ whiteSpace: "pre-line" }}>
                      <MultiLineResponse
                        keyHeader="specialReason"
                        value={naWrapper(detail.specialReason)}
                      />
                    </Typography>
                  </ResponseBlock>
                </Collapsible>
                <Collapsible header="小論文">
                  <ResponseBlock title="米国大学進学希望理由と将来の抱負">
                    <Typography style={{ whiteSpace: "pre-line" }}>
                      <MultiLineResponse
                        keyHeader="paper1"
                        value={naWrapper(detail.paper1)}
                      />
                    </Typography>
                  </ResponseBlock>
                </Collapsible>
                <Collapsible header="推薦状１">
                  {getFileViewer("推薦状１")}
                </Collapsible>
                <Collapsible header="推薦状２">
                  {getFileViewer("推薦状２")}
                </Collapsible>
                <Collapsible header="留学中の調査書">
                  {getFileViewer("留学中の調査書")}
                </Collapsible>
                <Collapsible header="調査書">
                  {getFileViewer("調査書")}
                </Collapsible>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xl={1} xs={12} />
        </Grid>
      </Box>
    </Box>
  );
};

export default Application;
